import { useEffect } from 'react';
import { X } from 'lucide-react';
import ReadingPane from './ReadingPane';

interface MailModalProps {
  accountId: string;
  messageId: string;
  provider: string;
  onClose: () => void;
  onQuickAction?: (mailData: { message: any; accountId: string }) => void;
}

export default function MailModal({ accountId, messageId, provider, onClose, onQuickAction }: MailModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscape);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
      />
      
      {/* Modal Container */}
      <div 
        className="relative w-full h-full sm:w-[90vw] sm:h-[85vh] sm:max-w-4xl sm:rounded-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{ 
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid var(--border-default)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile-optimized close button (floating, always visible) */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full transition-colors sm:hidden"
          style={{ 
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-secondary)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
          aria-label="Schließen"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Reading Pane Content */}
        <div className="h-full overflow-y-auto">
          <ReadingPane
            accountId={accountId}
            messageId={messageId}
            provider={provider}
            onClose={onClose}
            onQuickAction={onQuickAction}
          />
        </div>
      </div>
    </div>
  );
}
